import { WidgetAutocheck } from "@redotech/redo-model/widget";
import {
  itemValidForFinalSaleReturns,
  itemValidForProtection,
  itemValidForReturns,
} from "@redotech/redo-shopify-utils/check-product";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  ExtensionShopifyClient,
  getTags,
  ShopifyCartItem,
} from "@redotech/shopify-client/ajax";
import {
  getCollections,
  ShopifyExtensionClient,
} from "../client/shopify-extension-client";

export async function itemValidForReturnsFrontend({
  autocheck,
  item,
  redoShopifyExtensionClient,
  shopifyClient,
  signal,
  invalidReturnProductTags,
  invalidProtectionProductTags,
}: {
  autocheck: WidgetAutocheck;
  item: ShopifyCartItem;
  redoShopifyExtensionClient: ShopifyExtensionClient;
  shopifyClient: ExtensionShopifyClient;
  signal?: AbortSignal;
  invalidReturnProductTags: string[];
  invalidProtectionProductTags?: string[];
}): Promise<{
  returnsValid: boolean;
  protectionValid: boolean;
  finalSaleReturnsValid: boolean;
}> {
  if (location?.host === "checkout.denimtears.com") {
    return {
      returnsValid: true,
      protectionValid: true,
      finalSaleReturnsValid: false,
    };
  }
  if (globalThis.Shopify?.Checkout?.apiHost == "ensorings.myshopify.com") {
    return {
      returnsValid: true,
      protectionValid: true,
      finalSaleReturnsValid: false,
    };
  }

  const returnStorageValue = await getLocalStorageWithExpiry(
    `redo-return-valid-${item.product_id}`,
  );
  const protectionStorageValue = await getLocalStorageWithExpiry(
    `redo-protection-valid-${item.product_id}`,
  );
  const finalSaleStorageValue = await getLocalStorageWithExpiry(
    `redo-final-sale-valid-${item.product_id}`,
  );

  if (
    returnStorageValue !== null &&
    protectionStorageValue !== null &&
    finalSaleStorageValue !== null
  ) {
    return {
      returnsValid: returnStorageValue === "true",
      protectionValid: protectionStorageValue === "true",
      finalSaleReturnsValid: finalSaleStorageValue === "true",
    };
  }

  const product = await shopifyClient.productGet(item.handle, signal);
  const productTags = getTags(product);

  let collections: any[] = [];
  if (
    autocheck.coverageExcludeCollections.length ||
    autocheck.finalSaleReturns.coverageValidCollections.length
  ) {
    if (globalThis.redoStorefront?.type === "bigcommerce") {
      collections =
        (await shopifyClient.getCollections(product.id.toString())) ?? [];
    } else {
      collections =
        (await getCollections(redoShopifyExtensionClient, {
          signal,
          productId: String(item.product_id),
        })) ?? [];
    }
  }

  const properties: string[] = item.properties
    ? Object.keys(item.properties).reduce((acc, key) => {
        const value = item.properties?.[key];
        acc.push(key);
        if (value !== null && value !== "") {
          acc.push(`${key}: ${value}`);
        }
        return acc;
      }, [] as string[])
    : [];

  const hasValidReturnProduct = itemValidForReturns({
    coverageExcludeProperties: autocheck.coverageExcludeProperties,
    coverageExcludeCollections: autocheck.coverageExcludeCollections,
    invalidProductTags: invalidReturnProductTags,
    itemInfo: {
      properties,
      productTags,
      collections: collections.map((c) => c.title),
    },
  });

  const hasValidProtectionProduct = itemValidForProtection({
    invalidProductTags: invalidProtectionProductTags || [],
    itemInfo: {
      properties,
      productTags,
      collections: collections.map((c) => c.title),
    },
  });

  const hasValidFinalSaleProduct = itemValidForFinalSaleReturns({
    validProductTags: autocheck.finalSaleReturns.coverageValidTags,
    validCollections: autocheck.finalSaleReturns.coverageValidCollections,
    itemInfo: { productTags, collections: collections.map((c) => c.handle) },
  });

  setLocalStorageWithExpiry(
    `redo-return-valid-${item.product_id}`,
    String(hasValidReturnProduct),
  );

  setLocalStorageWithExpiry(
    `redo-protection-valid-${item.product_id}`,
    String(hasValidProtectionProduct),
  );

  setLocalStorageWithExpiry(
    `redo-final-sale-valid-${item.product_id}`,
    String(hasValidFinalSaleProduct),
  );

  return {
    returnsValid: hasValidReturnProduct,
    protectionValid: true,
    finalSaleReturnsValid: hasValidFinalSaleProduct,
  };
}
